import { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Stack,
  Link,
  Alert,
  AlertTitle,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  MessageSquare,
  Briefcase,
  FileText,
  Building,
  CreditCard,
  MessageCircle,
} from "lucide-react";
import { FormPhoneInput } from "../components/FormPhoneInput";

const Contact = () => {
  const theme = useTheme();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);

      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: "",
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <Box sx={{pb: 6, bgcolor: "grey.50" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
          pt: { xs: 14, lg: 18 },
          pb: { xs: 8, lg: 12 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box textAlign="center" color="white">
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              Bizimle İletişime Geçin
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 700,
                mx: "auto",
                opacity: 0.95,
                fontWeight: 400,
                lineHeight: 1.8,
              }}
            >
              Sorularınız ve talepleriniz için buradayız.
              <br />
              Size en kısa sürede
              dönüş yapacağız.
            </Typography>
          </Box>
        </Container>
      </Box>



      {/* İletişim Bilgilerimiz Detaylı Bölüm */}
      <Container maxWidth="lg" sx={{ mt: 10, mb: 8 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            İletişim Bilgilerimiz
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: "auto" }}>
            Bize ulaşmanın tüm yolları ve detaylı bilgiler
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Telefon ile ulaşın */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                    }}
                  >
                    <Phone size={28} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Telefon ile Ulaşın
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Müşteri hizmetlerimiz hafta içi 09:00-17:45 saatleri arasında hizmet vermektedir.
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      WhatsApp Destek
                    </Typography>
                    <Link
                      href="https://wa.me/905554890211"
                      target="_blank"
                      underline="none"
                      sx={{
                        color: theme.palette.info.main,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": { color: theme.palette.info.dark },
                      }}
                    >
                      <MessageCircle size={16} />
                      +90 555 489 02 11
                    </Link>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Çağrı Hattı
                    </Typography>
                    <Link
                      href="tel:+905321589467"
                      underline="none"
                      sx={{
                        color: theme.palette.info.main,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": { color: theme.palette.info.dark },
                      }}
                    >
                      <Phone size={16} />
                      +90 532 158 94 67
                    </Link>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Sabit Hat
                    </Typography>
                    <Link
                      href="tel:+908502444550"
                      underline="none"
                      sx={{
                        color: theme.palette.info.main,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": { color: theme.palette.info.dark },
                      }}
                    >
                      <Building size={16} />
                      +90 850 244 45 50
                    </Link>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Mail ile Ulaşın */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                    }}
                  >
                    <Mail size={28} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Mail ile Ulaşın
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Mail yoluyla haftanın 7 günü 24 saat bizimle irtibat kurabilirsiniz.
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Müşteri Hizmetleri
                    </Typography>
                    <Link
                      href="mailto:info@superdernek.com"
                      underline="none"
                      sx={{
                        color: theme.palette.info.main,
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        "&:hover": { color: theme.palette.info.dark },
                      }}
                    >
                      info@superdernek.com
                    </Link>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Kariyer Olanakları */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                    }}
                  >
                    <Briefcase size={28} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Kariyer Olanakları
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Şirketimiz bünyesinde; Yazılım departmanı, müşteri hizmetleri departmanı ve satış departmanında çalışmak üzere aşağıda yer alan adrese özgeçmişinizi gönderebilirsiniz.
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    İş Başvurusu
                  </Typography>
                  <Link
                    href="mailto:cv@ronesis.com"
                    underline="none"
                    sx={{
                      color: theme.palette.info.main,
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      "&:hover": { color: theme.palette.info.dark },
                    }}
                  >
                    cv@ronesis.com
                  </Link>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Şirket Bilgileri */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                    }}
                  >
                    <FileText size={28} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Şirket Bilgileri
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Unvanı:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      Rönesans İnovasyon ve Mühendislik A.Ş.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Ticari Sicil Numarası:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.info.main }}>
                      22229
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Mersis Numarası:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.info.main }}>
                      0735135892100001
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      KEP Adresi:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e1d1d" }}>
                      ronesansinovasyonvemuhendislik@hs01.kep.tr
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Kayıtlı Olduğu Oda:
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      Aydın Ticaret Odası
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Adres Bilgileri */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                    }}
                  >
                    <MapPin size={28} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Adres Bilgileri
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Müşteri Hizmetleri
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 3, lineHeight: 1.7 }}>
                  Güzelhisar Mah. Albay Şefik Cad. No:4 Daire:6 Efeler/AYDIN
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      İletişim Hatları
                    </Typography>
                    <Stack spacing={1}>
                      <Link
                        href="https://wa.me/905554890211"
                        target="_blank"
                        underline="none"
                        sx={{
                          color: theme.palette.info.main,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          "&:hover": { color: theme.palette.info.dark },
                        }}
                      >
                        <MessageCircle size={14} />
                        WhatsApp: +90 555 489 02 11
                      </Link>
                      <Link
                        href="tel:+905321589467"
                        underline="none"
                        sx={{
                          color: theme.palette.info.main,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          "&:hover": { color: theme.palette.info.dark },
                        }}
                      >
                        <Phone size={14} />
                        Telefon: +90 532 158 94 67
                      </Link>
                      <Link
                        href="tel:+908502444550"
                        underline="none"
                        sx={{
                          color: theme.palette.info.main,
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          "&:hover": { color: theme.palette.info.dark },
                        }}
                      >
                        <Building size={14} />
                        Sabit Hat: +90 850 244 45 50
                      </Link>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Bilgi ve Dökümanlar */}
          <Grid item xs={12} sm={6} lg={4}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      color: theme.palette.info.main,
                    }}
                  >
                    <CreditCard size={28} />
                  </Box>
                  <Typography variant="h6" fontWeight={700}>
                    Bilgi ve Dökümanlar
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
                  Şirketimiz, E-Fatura kullanıcısıdır.
                  E-Fatura kullanıcısı iş ortaklarımızın posta yoluyla fatura göndermesine gerek yoktur.
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                      Vergi Numarası:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e1d1d" }}>
                      7351358921
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                      Vergi Dairesi:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e1d1d" }}>
                      Güzelhisar V.D
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                      Hesap Numaramız:
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.info.main }}>
                        Alıcı Bilgisi: Rönesans İnovasyon ve Mühendislik A.Ş.
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.info.main }}>
                        Banka: Vakıfbank
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.info.main }}>
                        IBAN: TR39 0001 5001 5800 7365 5409 80
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 8, mb: 8, position: "relative", zIndex: 2 }}>
        <Grid container spacing={4} justifyContent="center">
          {/* Contact Form */}
          <Grid item xs={12} md={10} lg={8}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 1,
                boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                  p: 4,
                  color: "white",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <MessageSquare size={24} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="700">
                      Mesaj Gönderin
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                      Formu doldurun, size ulaşalım
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Ad Soyad"
                      name="name"
                      value={formData.name}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, "");
                        setFormData((prev) => ({ ...prev, name: value }));
                      }}
                      required
                      placeholder="Adınızı ve soyadınızı girin"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "white",
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="E-posta"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="ornek@email.com"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "white",
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />

                    <FormPhoneInput
                      name="phone"
                      value={String(formData.phone || "")}
                      handleChange={(e) => {
                        const phoneValue = typeof e === "object" && e?.target 
                          ? String(e.target.value || "") 
                          : String(e || "");
                        setFormData((prev) => ({ ...prev, phone: phoneValue }));
                      }}
                       sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "white",
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Firma"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Firma adınız (opsiyonel)"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "white",
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Konu"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Mesajınızın konusu"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "white",
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Mesajınız"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={5}
                      placeholder="Mesajınızı buraya yazın..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#fafafa",
                          "&:hover": {
                            bgcolor: "white",
                          },
                          "&.Mui-focused": {
                            bgcolor: "white",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderWidth: 2,
                              borderColor: theme.palette.info.main,
                            },
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />

                    <Button
                      aria-label="Mesaj Gönder"
                      type="submit"
                      fullWidth
                      disabled={isSubmitting}
                      size="large"
                      sx={{
                        py: 2,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                        color: "white",
                        textTransform: "none",
                        boxShadow: "0 4px 16px rgba(148, 13, 88, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 24px rgba(148, 13, 88, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          background: "grey.300",
                          color: "grey.600",
                        },
                        transition: "all 0.3s ease",
                      }}
                      startIcon={isSubmitting ? null : <Send size={20} />}
                    >
                      {isSubmitting ? "Gönderiliyor..." : "Mesaj Gönder"}
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Toast */}
      {showToast && (
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            top: 100,
            right: 24,
            zIndex: 9999,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            minWidth: 320,
            borderRadius: 2,
          }}
          onClose={() => setShowToast(false)}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>Mesajınız alındı!</AlertTitle>
          En kısa sürede size dönüş yapacağız.
        </Alert>
      )}
    </Box>
  );
};

export default Contact;